<template>
    <div
        v-click-outside="handleClickOutside"
        class="popup-button"
        :class="containerClass"
        @focusin="handleFocusIn"
        @focusout="handleFocusOut">
        <button
            ref="button"
            type="button"
            class="popup-toggle"
            :title="title"
            :disabled="disabled"
            :class="buttonClass"
            @click="togglePopup">
            <slot>
                Toggle
            </slot>
        </button>
        <div
            v-if="!lazy || popupVisible"
            v-show="popupVisible"
            ref="popup"
            tabindex="-1"
            class="popup-content"
            :class="[{ 'popup-content-with-arrow': isArrowEnabled }, popupClass]">
            <div v-if="isArrowEnabled" ref="arrow" class="popup-arrow"/>
            <slot name="popup" :close="closePopup">
                Popup content
            </slot>
        </div>
    </div>
</template>

<script>
import { createPopper } from '@popperjs/core';

export default {
    name: 'ButtonPopup',
    props: {
        popperOptions: {
            type: Object,
            default: () => ({})
        },
        containerClass: {
            type: [String, Array, Object],
            default: ''
        },
        buttonClass: {
            type: [String, Array, Object],
            default: ''
        },
        popupClass: {
            type: [String, Array, Object],
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        lazy: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            popupVisible: false,
            popperInstance: null,
            defaultPopperOptions: {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 2]
                        }
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'window',
                            padding: 10
                        }
                    },
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top', 'right', 'left', 'bottom']
                        }
                    }
                ]
            }
        };
    },
    computed: {
        isArrowEnabled() {
            const arrowModifier = (this.popperOptions.modifiers || []).find(
                (modifier) => modifier.name === 'arrow'
            );

            return !arrowModifier || arrowModifier.enabled !== false;
        }
    },
    methods: {
        togglePopup() {
            this.popupVisible ? this.closePopup() : this.openPopup();
        },
        openPopup() {
            if (this.disabled || this.popupVisible) {
                return;
            }

            this.popupVisible = true;
            document.addEventListener('keydown', this.handleKeydown);
            this.$nextTick(() => {
                if (this.popupVisible) {
                    this.createPopper();
                    this.$emit('open');
                }
            });
        },
        handleKeydown(event) {
            if (event.key === 'Escape' || event.key === 'Esc') {
                event.preventDefault();
                this.closePopup({ restoreFocus: true });
            }
        },
        handleFocusIn(event) {
            if (this.disabled || this.popupVisible) {
                return;
            }
            if (!event.relatedTarget || this.$el.contains(event.relatedTarget)) {
                return;
            }
            // Only auto-open for keyboard/programmatic focus; a mouse click
            // will open it via togglePopup instead.
            if (!this.$refs.button.matches(':focus-visible')) {
                return;
            }
            this.openPopup();
        },
        createPopper() {
            this.destroyPopper();

            this.popperInstance = createPopper(
                this.$refs.button,
                this.$refs.popup,
                this.buildPopperOptions()
            );
        },
        mergeModifier(modifierMap, modifier) {
            const existing = modifierMap.get(modifier.name);

            modifierMap.set(modifier.name, {
                ...(existing || {}),
                ...modifier,
                options: {
                    ...((existing && existing.options) || {}),
                    ...(modifier.options || {})
                }
            });
        },
        buildPopperOptions() {
            const modifierMap = new Map();

            this.defaultPopperOptions.modifiers.forEach((modifier) => {
                this.mergeModifier(modifierMap, modifier);
            });

            (this.popperOptions.modifiers || []).forEach((modifier) => {
                this.mergeModifier(modifierMap, modifier);
            });

            if (this.isArrowEnabled) {
                const placement = this.popperOptions.placement;
                const xDistance = 9;
                const yDistance = 8;
                let offset = [0, yDistance];

                if (placement === 'top-start' || placement === 'bottom-start') {
                    offset = [-xDistance, yDistance];
                }
                if (placement === 'top-end' || placement === 'bottom-end') {
                    offset = [xDistance, yDistance];
                }
                this.mergeModifier(modifierMap, {
                    name: 'offset',
                    options: {
                        offset
                    }
                });
                this.mergeModifier(modifierMap, {
                    name: 'arrow',
                    options: {
                        element: this.$refs.arrow
                    }
                });
            }

            return {
                ...this.defaultPopperOptions,
                ...this.popperOptions,
                modifiers: Array.from(modifierMap.values())
            };
        },
        destroyPopper() {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
        handleClickOutside() {
            if (this.popupVisible) {
                this.closePopup();
            }
        },
        handleFocusOut(event) {
            if (!this.popupVisible) {
                return;
            }
            if (event.relatedTarget && this.$el.contains(event.relatedTarget)) {
                return;
            }
            this.closePopup();
        },
        closePopup({ restoreFocus = false } = {}) {
            if (!this.popupVisible) {
                return;
            }

            this.popupVisible = false;
            document.removeEventListener('keydown', this.handleKeydown);
            this.destroyPopper();
            this.$emit('close');

            if (restoreFocus) {
                this.$nextTick(() => {
                    const nestedInput = this.$refs.button
                        && this.$refs.button.querySelector('input, textarea, [contenteditable="true"]');
                    const target = nestedInput || this.$refs.button;
                    if (target && typeof target.focus === 'function') {
                        target.focus();
                    }
                });
            }
        }
    },
    beforeDestroy() {  // Vue 3 Rename beforeUnmount
        document.removeEventListener('keydown', this.handleKeydown);
        this.destroyPopper();
    }
};
</script>
